import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { HiListBullet, HiViewColumns, HiChevronLeft, HiChevronRight, HiExclamationTriangle } from 'react-icons/hi2';
import { useDateFormat } from '@/utils/dates';
import { PageContent, PageHeader } from '@/components/layout/PageLayout';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import SprawaDetailModal from '@/components/sprawy/SprawaDetailModal';
import PosiedzenieFormModal from '@/components/sprawy/PosiedzenieFormModal';
import { DesktopOnly, MobileOnly } from '@/styles/shared.styled';
import {
  useListCalendarPosiedzeniaQuery,
  useCreatePosiedzenieMutation,
} from '@/store/sprawyApi';
import type { CalendarPosiedzenieDto, CreatePosiedzenieRequest } from '@/types';
import {
  CalendarWrapper,
  MonthNav,
  MonthTitle,
  CalendarGrid,
  DayHeader,
  DayCell,
  DayNumber,
  CalendarEvent,
  CollisionBadge,
  MobileEventList,
  MobileEventCard,
  MonthNavSpacer,
  MobileMonthTitle,
  DayCellOverflow,
  MobileEventTime,
  MobileEventSubject,
  MobileEventLocation,
} from './SprawyCalendarPage.styled';

function getMonthRange(year: number, month: number) {
  const from = new Date(year, month, 1);
  const to = new Date(year, month + 1, 1);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

function getCalendarDays(year: number, month: number) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  // Monday = 0, Sunday = 6
  let startOffset = firstDay.getDay() - 1;
  if (startOffset < 0) startOffset = 6;

  const days: { date: Date; isOutside: boolean }[] = [];

  // Previous month padding
  for (let i = startOffset - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({ date: d, isOutside: true });
  }

  // Current month
  for (let d = 1; d <= lastDay.getDate(); d++) {
    days.push({ date: new Date(year, month, d), isOutside: false });
  }

  // Next month padding to fill remaining cells
  const remainder = days.length % 7;
  if (remainder > 0) {
    const needed = 7 - remainder;
    for (let i = 1; i <= needed; i++) {
      days.push({ date: new Date(year, month + 1, i), isOutside: true });
    }
  }

  return days;
}

function dateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function SprawyCalendarPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatTime, formatDateTime, formatMonth } = useDateFormat();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [selectedSprawaId, setSelectedSprawaId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createInitialDate, setCreateInitialDate] = useState<string>('');

  const { from, to } = getMonthRange(year, month);
  const { data: posiedzenia, isLoading } = useListCalendarPosiedzeniaQuery({ from, to });
  const [createPosiedzenie] = useCreatePosiedzenieMutation();

  const days = useMemo(() => getCalendarDays(year, month), [year, month]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarPosiedzenieDto[]>();
    for (const p of posiedzenia ?? []) {
      const d = new Date(p.date);
      const key = dateKey(d);
      const list = map.get(key) ?? [];
      list.push(p);
      map.set(key, list);
    }
    return map;
  }, [posiedzenia]);

  const todayKey = dateKey(today);

  const dayKeys = [
    t('calendar.mon'), t('calendar.tue'), t('calendar.wed'), t('calendar.thu'),
    t('calendar.fri'), t('calendar.sat'), t('calendar.sun'),
  ];

  const monthLabel = formatMonth(new Date(year, month));

  const prevMonth = () => {
    if (month === 0) { setYear(year - 1); setMonth(11); }
    else setMonth(month - 1);
  };

  const nextMonth = () => {
    if (month === 11) { setYear(year + 1); setMonth(0); }
    else setMonth(month + 1);
  };

  const goToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); };

  const handleDayClick = (date: Date, isOutside: boolean) => {
    if (isOutside) return;
    setCreateInitialDate(new Date(date.getFullYear(), date.getMonth(), date.getDate(), 10, 0).toISOString());
    setShowCreateModal(true);
  };

  const handleEventClick = (e: React.MouseEvent, p: CalendarPosiedzenieDto) => {
    e.stopPropagation();
    setSelectedSprawaId(p.sprawaId);
  };

  const handleCreateSubmit = async (sprawaId: string, data: CreatePosiedzenieRequest) => {
    await createPosiedzenie({ sprawaId, ...data }).unwrap();
  };


  // Mobile: flat list of events for the month
  const sortedEvents = useMemo(() =>
    [...(posiedzenia ?? [])].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    [posiedzenia],
  );

  return (
    <PageContent>
      <PageHeader
        title={t('calendar.title')}
        subtitle={t('calendar.subtitle')}
        actions={
          <>
            <Button variant="outline-primary" onClick={() => navigate('/sprawy')}>
              <HiListBullet size="1rem" className="me-2" />
              {t('calendar.switchToList')}
            </Button>
            <Button variant="outline-primary" onClick={() => navigate('/sprawy/board')}>
              <HiViewColumns size="1rem" className="me-2" />
              {t('calendar.switchToBoard')}
            </Button>
          </>
        }
      />

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <DesktopOnly>
            <CalendarWrapper>
              <MonthNav>
                <div className="d-flex align-items-center gap-2">
                  <Button variant="outline-secondary" size="sm" onClick={prevMonth}>
                    <HiChevronLeft size="1rem" />
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={nextMonth}>
                    <HiChevronRight size="1rem" />
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={goToday}>
                    {t('calendar.today')}
                  </Button>
                </div>
                <MonthTitle>{monthLabel}</MonthTitle>
                <MonthNavSpacer />
              </MonthNav>

              <CalendarGrid>
                {dayKeys.map((d) => (
                  <DayHeader key={d}>{d}</DayHeader>
                ))}

                {days.map(({ date, isOutside }, idx) => {
                  const key = dateKey(date);
                  const events = eventsByDay.get(key) ?? [];
                  const isToday = key === todayKey;
                  const hasCollision = events.length >= 2;

                  return (
                    <DayCell
                      key={idx}
                      $isToday={isToday}
                      $isOutside={isOutside}
                      $hasCollision={hasCollision}
                      onClick={() => handleDayClick(date, isOutside)}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <DayNumber $isToday={isToday}>{date.getDate()}</DayNumber>
                        {hasCollision && (
                          <CollisionBadge title={t('calendar.collision')}>
                            <HiExclamationTriangle size="0.75rem" />
                            {events.length}
                          </CollisionBadge>
                        )}
                      </div>
                      {events.slice(0, 3).map((ev) => (
                        <CalendarEvent
                          key={ev.id}
                          $type={ev.type}
                          onClick={(e) => handleEventClick(e, ev)}
                          title={`${formatTime(ev.date)} — ${ev.sprawaSignature ?? ''} (${t(`posiedzenieType.${ev.type}`)})`}
                        >
                          {formatTime(ev.date)} {ev.sprawaSignature}
                        </CalendarEvent>
                      ))}
                      {events.length > 3 && (
                        <DayCellOverflow>+{events.length - 3}</DayCellOverflow>
                      )}
                    </DayCell>
                  );
                })}
              </CalendarGrid>
            </CalendarWrapper>
          </DesktopOnly>

          <MobileOnly>
            <MonthNav>
              <Button variant="outline-secondary" size="sm" onClick={prevMonth}>
                <HiChevronLeft size="1rem" />
              </Button>
              <MobileMonthTitle>{monthLabel}</MobileMonthTitle>
              <Button variant="outline-secondary" size="sm" onClick={nextMonth}>
                <HiChevronRight size="1rem" />
              </Button>
            </MonthNav>

            {sortedEvents.length === 0 ? (
              <p className="text-muted text-center mt-4">{t('calendar.noHearings')}</p>
            ) : (
              <MobileEventList className="mt-3">
                {sortedEvents.map((ev) => (
                  <MobileEventCard
                    key={ev.id}
                    $type={ev.type}
                    onClick={() => setSelectedSprawaId(ev.sprawaId)}
                  >
                    <MobileEventTime>{formatDateTime(ev.date)}</MobileEventTime>
                    <MobileEventSubject>
                      {ev.sprawaSignature} — {t(`posiedzenieType.${ev.type}`)}
                    </MobileEventSubject>
                    {ev.location && (
                      <MobileEventLocation>{ev.location}</MobileEventLocation>
                    )}
                  </MobileEventCard>
                ))}
              </MobileEventList>
            )}
          </MobileOnly>
        </>
      )}

      <SprawaDetailModal
        sprawaId={selectedSprawaId}
        onClose={() => setSelectedSprawaId(null)}
      />

      <PosiedzenieFormModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateSubmit}
        initialDate={createInitialDate}
      />
    </PageContent>
  );
}
